import { useQuery } from '@apollo/client'
import * as d3 from 'd3-hierarchy'
import { useMemo } from 'react'

import { IPrepItemCategoryType, getPrepItemCategory } from '../gql'

const usePrepItemCategoryDataSource = () => {
  const { data: prepItemCategoryData, loading: prepItemCategoryLoading } =
    useQuery<IPrepItemCategoryType>(getPrepItemCategory)

  const dataSource = useMemo(() => {
    if (!prepItemCategoryData?.getPrepItemCategory) return []

    const nodes = prepItemCategoryData.getPrepItemCategory.nodes

    // Add root node and prepare data for d3.stratify
    const hierarchyData = [
      { id: 'root', parentId: null, name: 'Root' },
      ...nodes.map((node) => ({
        id: node.prepItemIdCode,
        parentId:
          node.parentPrepCategoryIdCode === 'root'
            ? 'root'
            : node.parentPrepCategoryIdCode,
        name: node.prepItemName,
      })),
    ]

    // Create hierarchy using d3.stratify
    const root = d3.stratify<typeof hierarchyData[0]>()(hierarchyData)

    // Create a map for quick category name lookup
    const categoryMap = new Map<string, string>()
    root.descendants().forEach((node) => {
      if (node.data.id !== 'root') {
        categoryMap.set(node.data.id, node.data.name)
      }
    })

    // Get all leaf nodes (prep items that have no children)
    const leafNodes = root
      .leaves()
      .filter((leaf) => leaf.data.id !== 'root')
      .map((leaf) => ({
        key: leaf.data.id,
        prepItem: leaf.data.name,
        // Get parent's name from the hierarchy
        category: leaf.parent
          ? categoryMap.get(leaf.parent.data.id) || leaf.parent.data.id
          : '',
        categoryKey: leaf.parent ? leaf.parent.data.id : '',
      }))

    return leafNodes
  }, [prepItemCategoryData])

  return {
    loading: prepItemCategoryLoading,
    dataSource,
  }
}

export default usePrepItemCategoryDataSource
